import { useState, useMemo, useEffect } from 'react';
import { useConversations } from '@/hooks/useMessages';
import { useMessagingSocket } from '@/hooks/useMessagingSocket';
import { toast } from '@/utils/toast';
import type { MessageWithDetails, ConversationWithPreview } from '@/types/messaging';

import Button, { IconButton } from '../common/Button';
import SearchInput from '../common/SearchInput';
import FaIcon from '../common/FaIcon';
import Modal from '../common/Modal';

interface ForwardMessageModalProps {
  message: MessageWithDetails;
  currentConversationId: string;
  onClose: () => void;
}

export function ForwardMessageModal({ message, currentConversationId, onClose }: ForwardMessageModalProps) {
  const [search, setSearch] = useState('');
  const [selectedConversations, setSelectedConversations] = useState<string[]>([]);
  const [isForwarding, setIsForwarding] = useState(false);
  const { data: conversationsData, isLoading: conversationsLoading } = useConversations();
  const { sendMessage, socket } = useMessagingSocket();

  // Filter conversations (exclude current one)
  const filteredConversations = useMemo(() => {
    if (!conversationsData?.data) return [];

    let conversations = conversationsData.data.filter(c => c.id !== currentConversationId);

    if (search.trim()) {
      const searchLower = search.toLowerCase();
      conversations = conversations.filter(c => {
        const name = getConversationName(c).toLowerCase();
        return name.includes(searchLower);
      });
    }

    return conversations;
  }, [conversationsData?.data, currentConversationId, search]);

  const toggleConversation = (conversationId: string) => {
    setSelectedConversations(prev =>
      prev.includes(conversationId)
        ? prev.filter(id => id !== conversationId)
        : [...prev, conversationId]
    );
  };

  const handleForward = () => {
    if (selectedConversations.length === 0) return;

    if (!socket?.connected) {
      toast.error('Not connected — unable to forward message');
      return;
    }

    setIsForwarding(true);
    let failedCount = 0;

    for (const conversationId of selectedConversations) {
      if (!socket?.connected) {
        failedCount += 1;
        continue;
      }

      const forwardedContent = `[Forwarded message]\n${message.content || ''}`;

      sendMessage({
        conversation_id: conversationId,
        content: forwardedContent,
        type: 'text',
        metadata: {
          forwarded_from: {
            conversation_id: currentConversationId,
            message_id: message.id,
            sender_name: message.sender?.name,
          },
        },
      });
    }

    setIsForwarding(false);

    if (failedCount > 0) {
      toast.error(`Failed to forward to ${failedCount} conversation${failedCount > 1 ? 's' : ''} — connection lost`);
    } else {
      toast.success(`Message forwarded to ${selectedConversations.length} conversation${selectedConversations.length > 1 ? 's' : ''}`);
    }

    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} title="Forward Message" size="md">

        {/* Message preview */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-theme-primary">
          <p className="text-xs text-theme-tertiary mb-1">
            From: {message.sender?.name || 'Unknown'}
          </p>
          <p className="text-sm text-theme-secondary line-clamp-3">
            {message.content}
          </p>
        </div>

        {/* Search */}
        <div className="px-4 py-2 border-b border-theme-primary">
          <SearchInput
            placeholder="Search conversations..."
            aria-label="Search conversations"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto">
          {conversationsLoading ? (
            // "No conversations found" was the answer shown while the list was
            // still loading, which is the wrong one.
            <div className="flex flex-col items-center justify-center py-8 text-theme-tertiary">
              <p className="text-sm">Loading conversations...</p>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-theme-tertiary">
              <p className="text-sm">No conversations found</p>
            </div>
          ) : (
            filteredConversations.map((conversation) => (
              <button
                key={conversation.id}
                onClick={() => toggleConversation(conversation.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  selectedConversations.includes(conversation.id)
                    ? 'bg-primary-50 dark:bg-gray-700'
                    : ''
                }`}
              >
                {/* Checkbox */}
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedConversations.includes(conversation.id)
                      ? 'bg-primary-500 border-primary-500'
                      : 'border-theme-secondary'
                  }`}
                >
                  {selectedConversations.includes(conversation.id) && (
                    <FaIcon icon="check" className="w-3 h-3 text-white" />
                  )}
                </div>

                {/* Avatar */}
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                    conversation.type === 'direct' ? 'bg-primary-500' :
                    conversation.type === 'group' ? 'bg-purple-500' :
                    'bg-orange-500'
                  }`}
                >
                  {conversation.type === 'incident' ? '!' :
                   conversation.type === 'group' ? '#' :
                   getConversationName(conversation).charAt(0).toUpperCase()}
                </div>

                {/* Name */}
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-theme-primary truncate">
                    {getConversationName(conversation)}
                  </p>
                  {conversation.type !== 'direct' && conversation.participant_count && (
                    <p className="text-xs text-theme-tertiary">
                      {conversation.participant_count} members
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-theme-primary flex items-center justify-between">
          <span className="text-sm text-theme-tertiary">
            {selectedConversations.length} selected
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleForward}
              disabled={selectedConversations.length === 0 || isForwarding}
              loading={isForwarding}
            >
              {isForwarding ? 'Forwarding...' : `Forward${selectedConversations.length > 0 ? ` (${selectedConversations.length})` : ''}`}
            </Button>
          </div>
        </div>
    </Modal>
  );
}

function getConversationName(conversation: ConversationWithPreview): string {
  if (conversation.type === 'direct' && conversation.other_participant) {
    return conversation.other_participant.name;
  }
  return conversation.name || 'Unnamed conversation';
}
